/**
 * Field lengths, mirrored from functions/src/validation/fieldLimits.ts.
 *
 * This copy exists so an input can stop at the limit instead of letting
 * somebody write four hundred characters of policy title and learn the ceiling
 * from a rejection after they press the button. It is NOT validation: every
 * callable re-checks its own input server-side, and must, because nothing a
 * browser enforces survives devtools.
 *
 * KEEP THE NUMBERS IDENTICAL. A client limit lower than the server's silently
 * forbids something the product allows; higher, and the field accepts text the
 * save will refuse — which is the bug this file was written to fix, from the
 * other direction. `fieldLimits.mirror.test.ts` reads the backend file and
 * compares, so the two cannot drift quietly.
 *
 * The comment on each constant names the standard the backend cites, because a
 * number with no source is one somebody will "round up" later.
 */

/** RFC 5321 §4.5.3.1.3 — the longest path an SMTP server must accept. */
export const EMAIL_MAX = 254;
/** A DNS name, RFC 1035. */
export const DOMAIN_MAX = 255;

/** ISO 20022 `Max140Text`, used for party names. */
export const ORG_NAME_MAX = 140;

/** ISO 20022 `Max140Text` territory, narrowed: no person's name needs more. */
export const PERSON_NAME_MAX = 100;

/** No standard. Set above the longest real policy titles in the seed data. */
export const TITLE_MAX = 200;

/** No standard. Two full sentences, and short enough to stay scannable. */
export const SUMMARY_MAX = 500;

/** A department, category or document type name — a label in a dropdown. */
export const TAXONOMY_NAME_MAX = 100;

/** Its short code — "NUR", "PHARM" — read aloud and used in policy numbers. */
export const TAXONOMY_CODE_MAX = 20;

/** Firestore document ids and the like. */
export const ID_MAX = 128;

/** Short human-assigned codes: department codes, policy number prefixes. */
export const CODE_MAX = 64;

/** Free text on a taxonomy entry or a workflow step. */
export const DESCRIPTION_MAX = 2000;

/** ITU-T E.164 plus separators and an extension. */
export const PHONE_MAX = 30;

/** A job title on a contact or a member. */
export const JOB_TITLE_MAX = 100;

/** Our own note on a person at a customer, or where an executed copy lives. */
export const CONTACT_NOTE_MAX = 500;
export const PAPERWORK_REFERENCE_MAX = 500;

/** RFC 3986 gives no limit; this is what browsers and proxies agree on. */
export const URL_MAX = 2048;

/** Credentials and other opaque strings. */
export const SECRET_MAX = 512;

/** What a person types into a search or filter box. */
export const SEARCH_QUERY_MAX = 256;

/**
 * A filter that never leaves the browser.
 *
 * Shorter than SEARCH_QUERY_MAX on purpose: these narrow a list already on
 * screen, so nothing is sent anywhere and a hundred characters is far past
 * the point of a useful filter. It exists so the box has SOME ceiling — an
 * unbounded input is a paste of a whole document away from a janky page.
 */
export const FILTER_MAX = 100;

// ---------------------------------------------------------------- scope of service

/** A hospital unit's name. TITLE_MAX's size: it is shown where a title is. */
export const SCOPE_UNIT_NAME_MAX = 200;

/** A unit code — "ED-001", "CVICU" — with room for a site prefix. */
export const SCOPE_UNIT_CODE_MAX = 50;

/** A narrative section of a scope: about a typed page. */
export const SCOPE_TEXT_MAX = 5000;

/** Hours of operation: "24/7", or a week's schedule spelled out. */
export const SCOPE_HOURS_MAX = 1000;

/** One entry in the patient-populations or services-provided list. */
export const SCOPE_LIST_ITEM_MAX = 200;

/** How many patient populations one unit may list. */
export const SCOPE_POPULATIONS_MAX = 50;

/** How many services one unit may list. */
export const SCOPE_SERVICES_MAX = 100;

/** A rejection reason, or a new version's change notes. */
export const SCOPE_REASON_MAX = 2000;

/** A whole CSV import, as text. */
export const SCOPE_CSV_MAX = 500_000;

/** Where the unit physically is. Matches SCOPE_UNIT_NAME_MAX. */
export const SCOPE_LOCATION_MAX = 200;
/** A unit leader with credentials and title. */
export const SCOPE_LEADER_MAX = 200;
/** Quality indicators a unit tracks. */
export const SCOPE_INDICATORS_MAX = 50;
/** Beds on a unit; the largest US hospital has about 2,600 in all. */
export const SCOPE_BEDS_MAX = 5000;

// ---------------------------------------------------------------- exceptions and variances

/** An exception's title. */
export const EXC_TITLE_MAX = 200;

/** The description of the deviation, and the justification: a couple of pages. */
export const EXC_TEXT_MAX = 10_000;

/** An impact assessment, the conditions to maintain, or a review's findings. */
export const EXC_ASSESSMENT_MAX = 5000;

/** One mitigating control, as a sentence. */
export const EXC_CONTROL_MAX = 500;

/** How many mitigating controls one exception may list. */
export const EXC_CONTROLS_MAX = 20;

/** Comments on an approval decision or a mismatch acknowledgement. */
export const EXC_COMMENT_MAX = 2000;

/** The most steps in an approval chain. */
export const EXC_CHAIN_MAX = 5;

/** The longest review interval, in days. */
export const EXC_REVIEW_DAYS_MAX = 365;

/** The most renewals an exception may allow. */
export const EXC_RENEWALS_MAX = 100;

/** A policy title copied onto the exception. */
export const EXC_POLICY_TITLE_MAX = 500;

/** The committee that oversees an exception. Matches EXC_TITLE_MAX. */
export const EXC_COMMITTEE_MAX = 200;

// ---------------------------------------------------------------- competency and training

/** A competency's title. */
export const COMP_TITLE_MAX = 200;

/** A description, an attestation statement, or evidence notes. */
export const COMP_TEXT_MAX = 5000;

/** A retirement or waiver reason, or a manager's comments on evidence. */
export const COMP_REASON_MAX = 2000;

/** The longest renewal interval, in days. */
export const COMP_RENEWAL_DAYS_MAX = 1095;

/** The longest default due window at publication, in days. */
export const COMP_DUE_DAYS_MAX = 365;

/** How many files one piece of evidence may carry. */
export const COMP_FILES_MAX = 20;

/** The assessor named on evidence, with credentials. */
export const COMP_ASSESSOR_MAX = 200;
/** A score on evidence is a percentage. */
export const COMP_SCORE_MAX = 100;

// ---------------------------------------------------------------- forms and attestations

/** A form's title. */
export const FORM_TITLE_MAX = 200;

/** A form's description. */
export const FORM_DESCRIPTION_MAX = 5000;

/** Instructions before the form, or the legal attestation statement. */
export const FORM_LONG_TEXT_MAX = 10_000;

/** A field's label — it can be a whole question. */
export const FORM_FIELD_LABEL_MAX = 500;

/** A field's description, shown under it. */
export const FORM_FIELD_DESCRIPTION_MAX = 2000;

/** A field's help text. */
export const FORM_FIELD_HELP_MAX = 1000;

/** One option of a choice field. */
export const FORM_OPTION_MAX = 500;

/** How many options a choice field may offer. */
export const FORM_OPTIONS_MAX = 100;

/** How many fields one form may have. */
export const FORM_FIELDS_MAX = 200;

/** A field id, generated by the builder. */
export const FORM_FIELD_ID_MAX = 100;

/** A regular expression a field may validate against. */
export const FORM_REGEX_MAX = 500;

/** A retirement or waiver reason. */
export const FORM_REASON_MAX = 2000;

/** The longest default due window at publication, in days. */
export const FORM_DUE_DAYS_MAX = 365;

/** The most people a form may name individually. */
export const FORM_TARGET_USERS_MAX = 500;

/** Months between signings of a recurring form; ten years. */
export const FORM_RENEWAL_MONTHS_MAX = 120;

/** A pasted staff list for bulk invitation: 200 rows of up to 200 characters. Mirrors the backend. */
export const STAFF_CSV_MAX = 40_000;

/** The most steps one approval workflow may hold. No standard exists; the longest real chain found has five. */
export const APPROVAL_STEPS_MAX = 10;

/** The most document types, or departments, one approval workflow may name. Mirrors the backend. */
export const COVERAGE_IDS_MAX = 100;

/** An approval workflow's name and its one-line description: sized to the list and the dropdown they are shown in. Mirrors the backend. */
export const WORKFLOW_NAME_MAX = 100;
export const WORKFLOW_SHORT_DESCRIPTION_MAX = 200;

/** Limits on one approval step. Mirror the backend. */
export const STEP_ASSIGNEES_MAX = 50;
export const STEP_REQUIRED_APPROVALS_MAX = 20;
export const STEP_DUE_DAYS_MAX = 365;
export const COMMITTEE_VOTE_MAX = 50;
export const COMMITTEE_MINUTES_REF_MAX = 200;
